(() => {
  'use strict';

  // RN diagnostics only. Shopify's native channel remains the owner of
  // page_view, view_item, add_to_cart, begin_checkout and purchase.
  // This file creates no state and emits nothing until affirmative analytics
  // consent is available for a customer-like session.
  if (window.__rnAnalyticsV1Loaded === true) return;
  window.__rnAnalyticsV1Loaded = true;

  const CONTRACT_VERSION = '2026-08-23-v1';
  const GA4_MEASUREMENT_ID = 'G-LGHZHFLDZN';
  const STORAGE_KEY = 'rn:attribution:v2';
  const WORLD_ENTRY_STORAGE_PREFIX = 'rn:world-entry:v2:';
  const INTERNAL_STORAGE_KEY = 'rn:internal-traffic:v1';
  const MAX_PATH_LENGTH = 160;
  const MAX_VALUE_LENGTH = 160;
  const MAX_TOUCHPOINTS = 10;
  const CART_TIMEOUT_MS = 2500;
  // Consent and Shopify's measurement bootstrap can settle on different
  // turns. Give the world-entry owner a short, bounded window to observe a
  // usable context and gtag without creating an unbounded timer loop.
  const WORLD_ENTRY_RETRY_DELAYS_MS = Object.freeze([50, 100, 250, 500, 1000, 2000]);

  const PERSONALISATION_FORM_EVENTS = new Set([
    'form_start', 'field_completed', 'validation_error',
    'add_to_cart_attempt', 'add_to_cart_success',
  ]);
  const NEWSLETTER_EVENTS = new Set(['newsletter_view', 'newsletter_signup']);
  const DIAGNOSTIC_EVENTS = new Set([
    ...PERSONALISATION_FORM_EVENTS,
    ...NEWSLETTER_EVENTS,
    'rn_world_entry',
    'rn_guide_product_click',
    'rn_personalisation_start',
    'rn_funnel_add_to_cart_attempt',
    'rn_add_to_cart_success',
    'rn_personalisation_validation_error',
    'rn_photo_selected',
    'rn_personalisation_completed',
    'rn_chat_opened',
    'rn_product_share_open',
    'rn_product_link_copy_attempt',
    'rn_customer_help_open',
    'rn_customer_help_click',
    'rn_care_guide_click',
    'rn_home_discovery_impression',
    'rn_home_discovery_destination_click',
    'rn_imagination_factory_click',
    'rn_memorial_shelf_click',
    'rn_hint_share',
    'rn_variant_select',
    'rn_photo_added',
    'rn_photo_later',
    'rn_review_write',
    'rn_reviews_jump',
    // 12 Oct 2026 buyer monitor (assets/rn-monitor.js, j1012-103738-ce65).
    'rn_click',
    'rn_cart_ok',
    'rn_cart_fail',
    'rn_js_error',
    'rn_resource_fail',
    'rn_error_shown',
  ]);

  const SAFE_PARAMETERS = new Set([
    'event_id',
    'event_version',
    'occurred_at_utc',
    'rn_visit_id',
    'rn_consent_state',
    'rn_surface',
    'rn_profile',
    'rn_route_id',
    'rn_route_path',
    'rn_product_id',
    'rn_variant_id',
    'rn_product_handle',
    'rn_family_id',
    'rn_cluster_id',
    'rn_world',
    'rn_selection_event_id',
    'rn_field_type',
    'form_id',
    'signup_source',
    'page_path',
    'field_name',
    'rn_input_type',
    'rn_source_family',
    'rn_source_platform',
    'rn_source_surface',
    'rn_source_confidence',
    'rn_source_host',
    'rn_utm_source',
    'rn_utm_medium',
    'rn_utm_campaign',
    'module_id',
    'cta_label',
    'rn_photo_count',
    'rn_el',
    'rn_op',
    'rn_status',
    'rn_ms',
    'rn_kind',
    'rn_src',
    'rn_msg',
  ]);

  const ALLOWED_SOURCE_HOSTS = Object.freeze({
    'google.com': { family: 'search', platform: 'google_search', surface: 'organic_web' },
    'google.co.uk': { family: 'search', platform: 'google_search', surface: 'organic_web' },
    'bing.com': { family: 'search', platform: 'bing_search', surface: 'organic_web' },
    'perplexity.ai': { family: 'ai', platform: 'perplexity', surface: 'ai_answer' },
    'copilot.microsoft.com': { family: 'ai', platform: 'microsoft_copilot', surface: 'ai_answer' },
    'chatgpt.com': { family: 'ai', platform: 'chatgpt', surface: 'ai_answer' },
    'chat.openai.com': { family: 'ai', platform: 'chatgpt', surface: 'ai_answer' },
    'gemini.google.com': { family: 'ai', platform: 'gemini', surface: 'ai_answer' },
    'bard.google.com': { family: 'ai', platform: 'gemini', surface: 'ai_answer' },
    'pinterest.com': { family: 'social', platform: 'pinterest', surface: 'referral' },
  });

  const ALLOWED_UTM_MEDIUMS = Object.freeze({
    chatgpt: { family: 'ai', platform: 'chatgpt', surface: 'ai_answer' },
    gemini: { family: 'ai', platform: 'gemini', surface: 'ai_answer' },
    perplexity: { family: 'ai', platform: 'perplexity', surface: 'ai_answer' },
    copilot: { family: 'ai', platform: 'microsoft_copilot', surface: 'ai_answer' },
    microsoft_copilot: { family: 'ai', platform: 'microsoft_copilot', surface: 'ai_answer' },
    'microsoft-copilot': { family: 'ai', platform: 'microsoft_copilot', surface: 'ai_answer' },
    google_ai: { family: 'ai', platform: 'google_ai', surface: 'ai_answer' },
    'google-ai': { family: 'ai', platform: 'google_ai', surface: 'ai_answer' },
    'ai-overview': { family: 'ai', platform: 'google_ai', surface: 'ai_answer' },
    'ai-mode': { family: 'ai', platform: 'google_ai', surface: 'ai_answer' },
  });

  const ALLOWED_SOURCE_FAMILIES = new Set([
    'search', 'merchant', 'ai', 'referral', 'social', 'direct', 'internal', 'unknown',
  ]);
  const ALLOWED_SOURCE_PLATFORMS = new Set([
    'google_search', 'bing_search', 'other', 'chatgpt', 'gemini', 'google_ai',
    'perplexity', 'microsoft_copilot', 'pinterest', 'unknown',
  ]);
  const ALLOWED_SOURCE_SURFACES = new Set([
    'organic_web', 'ai_answer', 'referral', 'unknown',
  ]);
  const ALLOWED_SOURCE_CONFIDENCE = new Set([
    'observed_allowlisted_utm', 'observed_allowlisted_referrer', 'observed_no_source',
  ]);
  const ALLOWED_INPUT_TYPES = new Set([
    'wording', 'photo', 'design_brief', 'proof_email', 'variant_conditional',
  ]);
  const ALLOWED_PROFILES = new Set([
    'wording', 'design_brief', 'wording_and_brief', 'photo_likeness',
    'photo_likeness_proof', 'variant_conditional',
  ]);
  const ALLOWED_MEMORIAL_SHELF_ACTIONS = new Set([
    'portrait', 'urn', 'garden', 'see_all_portrait', 'see_all_urn', 'see_all_garden',
  ]);
  const ALLOWED_HOME_DISCOVERY_ACTIONS = new Set([
    'collection_disco',
    'collection_sculpted_planters',
    'collection_planters_home_decor',
    'product_disco_era_bust',
    'product_golden_hour_planter',
  ]);

  // The adapter only observes customer-facing storefront routes. Customer
  // accounts, order status, checkout and platform/private endpoints are not a
  // measurement surface, even when their pathname happens to contain a
  // leading slash. Keep this allowlist open to future public storefront
  // handles without accepting arbitrary private routes.
  const PUBLIC_PATH_PATTERNS = Object.freeze([
    /^\/$/,
    /^\/products(?:\/|$)/,
    /^\/collections(?:\/|$)/,
    /^\/pages(?:\/|$)/,
    /^\/blogs(?:\/|$)/,
    /^\/blog(?:\/|$)/,
    /^\/search\/?$/,
    /^\/cart\/?$/,
  ]);

  const GUIDE_PRODUCT_CONTEXT_KEYS = new Set([
    'rn_product_id',
    'rn_variant_id',
    'rn_product_handle',
    'rn_family_id',
    'rn_cluster_id',
  ]);

  const WORLD_VALUES = Object.freeze({
    memorial: 'memorial',
    rat: 'rat',
    'rat care': 'rat',
    home: 'home_decor',
    home_decor: 'home_decor',
    'home & garden': 'home_decor',
    'home and garden': 'home_decor',
    'shared_or_unknown': 'shared_or_unknown',
  });

  const emittedKeys = new Set();
  const state = {
    eventSequence: 0,
    persistPromise: null,
    persistedSignature: '',
    worldEntryRecorded: new Set(),
    worldEntryEventIds: new Map(),
    worldEntryRetryTimer: null,
    worldEntryRetryIndex: 0,
  };

  const trim = (value, max = MAX_VALUE_LENGTH) => String(value || '').trim().slice(0, max);
  const normaliseToken = (value) => trim(value).toLowerCase();

  const publicStorefrontPath = (value = window.location?.pathname || '/') => {
    const path = String(value || '/').split(/[?#]/, 1)[0].trim();
    if (!path.startsWith('/')) return null;
    const bounded = path.slice(0, MAX_PATH_LENGTH).toLowerCase();
    return PUBLIC_PATH_PATTERNS.some((pattern) => pattern.test(bounded)) ? bounded : null;
  };

  const safePath = (value = window.location?.pathname || '/') => {
    return publicStorefrontPath(value) || '/';
  };

  const routeId = (path) => `route:${safePath(path)}`;

  const safeNumericId = (value) => {
    const raw = String(value || '').trim();
    return /^\d{1,30}$/.test(raw) ? raw : '';
  };

  const productGid = (value) => {
    const id = safeNumericId(value);
    return id ? `gid://shopify/Product/${id}` : null;
  };

  const variantGid = (value) => {
    const id = safeNumericId(value);
    return id ? `gid://shopify/ProductVariant/${id}` : null;
  };

  const safeHandle = (value) => {
    const handle = trim(value).toLowerCase();
    return /^[a-z0-9][a-z0-9-]{0,159}$/.test(handle) ? handle : null;
  };

  const safeOwnerKey = (value) => {
    const owner = normaliseToken(value);
    return /^[a-z0-9](?:[a-z0-9-]{0,158}[a-z0-9])?$/.test(owner) ? owner : null;
  };

  const safeClusterId = (value) => {
    const cluster = trim(value).toUpperCase();
    return /^C(?:[1-9]|1[0-5])$/.test(cluster) ? cluster : null;
  };

  const safeCampaign = (value) => {
    const campaign = trim(value, 80);
    return /^[A-Za-z0-9][A-Za-z0-9._-]{0,79}$/.test(campaign) ? campaign : null;
  };

  const handleFromPath = (path) => {
    const match = safePath(path).match(/^\/products\/([^/]+)/i);
    if (!match) return null;
    try {
      return safeHandle(decodeURIComponent(match[1]));
    } catch (_error) {
      return safeHandle(match[1]);
    }
  };

  const isInternalTraffic = () => {
    if (window.__rnInternalTraffic === true) return true;
    try {
      const params = new URLSearchParams(window.location?.search || '');
      if (
        params.get('rn_internal') === '1'
        || params.has('rn_qa')
        || params.has('rn_test')
        || params.has('preview_theme_id')
        || window.Shopify?.designMode === true
      ) return true;
    } catch (_error) {
      return true;
    }

    // The layout's internal marker is session-scoped. Reading it is safe;
    // this module never writes it and never creates attribution state for it.
    try {
      return window.sessionStorage?.getItem(INTERNAL_STORAGE_KEY) === '1';
    } catch (_error) {
      return true;
    }
  };

  const privacyApi = () => window.Shopify?.customerPrivacy || null;

  const consentState = () => {
    if (isInternalTraffic()) return 'internal';
    const privacy = privacyApi();
    if (
      !privacy
      || typeof privacy.analyticsProcessingAllowed !== 'function'
      || typeof privacy.currentVisitorConsent !== 'function'
    ) return 'unknown';

    try {
      const consent = privacy.currentVisitorConsent() || {};
      if (
        privacy.analyticsProcessingAllowed() === true
        && (consent.analytics === 'yes' || consent.analytics === true)
      ) return 'accepted';
      if (
        privacy.analyticsProcessingAllowed() === false
        || consent.analytics === 'no'
        || consent.analytics === false
      ) return 'rejected';
    } catch (_error) {
      return 'unknown';
    }
    return 'unknown';
  };

  const sessionStorageIfAvailable = () => {
    if (isInternalTraffic()) return null;
    try {
      const storage = window.sessionStorage;
      if (!storage) return null;
      // Read-only probe: do not create a probe key or attribution state.
      storage.getItem('__rn_attribution_storage_probe__');
      return storage;
    } catch (_error) {
      return null;
    }
  };

  const randomVisitId = () => {
    try {
      if (typeof window.crypto?.randomUUID === 'function') {
        const value = window.crypto.randomUUID().replace(/[^A-Za-z0-9_-]/g, '');
        return `rn1_${value}`.slice(0, 64);
      }
      if (typeof window.crypto?.getRandomValues !== 'function') return null;
      const bytes = new Uint8Array(16);
      window.crypto.getRandomValues(bytes);
      const value = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
      return `rn1_${value}`;
    } catch (_error) {
      return null;
    }
  };

  const eventId = (prefix = 'evt') => {
    state.eventSequence += 1;
    const visit = randomVisitId();
    const suffix = visit ? visit.slice(4) : String(state.eventSequence);
    return `${prefix}_${suffix}_${state.eventSequence}`.slice(0, 100);
  };

  const currentWorld = () => {
    const raw = document.body?.dataset?.rnWorld
      || document.querySelector?.('[data-rn-world]')?.dataset?.rnWorld
      || '';
    return WORLD_VALUES[normaliseToken(raw)] || 'shared_or_unknown';
  };

  const hostFromReferrer = () => {
    try {
      const referrer = document.referrer || '';
      if (!referrer) return '';
      const hostname = new URL(referrer).hostname.replace(/^www\./, '').toLowerCase();
      return Object.keys(ALLOWED_SOURCE_HOSTS).sort((left, right) => right.length - left.length).find((base) =>
        hostname === base || hostname.endsWith(`.${base}`)
      ) || '';
    } catch (_error) {
      return '';
    }
  };

  const sourceContext = () => {
    let params = null;
    try {
      params = new URLSearchParams(window.location?.search || '');
    } catch (_error) {
      params = null;
    }

    const explicitSource = normaliseToken(params?.get('rn_ai_source'));
    const explicitMap = explicitSource === 'chatgpt'
      ? ALLOWED_UTM_MEDIUMS.chatgpt
      : explicitSource === 'gemini'
        ? ALLOWED_UTM_MEDIUMS.gemini
        : explicitSource === 'google_ai' || explicitSource === 'google-ai'
          ? ALLOWED_UTM_MEDIUMS.google_ai
          : explicitSource === 'perplexity'
            ? ALLOWED_UTM_MEDIUMS.perplexity
            : ['copilot', 'microsoft_copilot', 'microsoft-copilot'].includes(explicitSource)
              ? ALLOWED_UTM_MEDIUMS.copilot
              : null;
    const utmSource = normaliseToken(params?.get('utm_source'));
    const utmMedium = normaliseToken(params?.get('utm_medium'));
    const rnUtmAllowed = utmSource === 'rn-ai' || utmSource === 'rn_ai';
    const utmMap = rnUtmAllowed ? ALLOWED_UTM_MEDIUMS[utmMedium] || null : null;
    const referrerHost = hostFromReferrer();
    const referrerMap = referrerHost ? ALLOWED_SOURCE_HOSTS[referrerHost] : null;
    const mapped = explicitMap || utmMap || referrerMap;
    const confidence = explicitMap || utmMap
      ? 'observed_allowlisted_utm'
      : referrerMap
        ? 'observed_allowlisted_referrer'
        : 'observed_no_source';

    return {
      source_family: mapped?.family || 'unknown',
      source_platform: mapped?.platform || 'unknown',
      source_surface: mapped?.surface || 'unknown',
      source_confidence: confidence,
      source_host: referrerHost || null,
      utm_source: rnUtmAllowed && utmMap ? 'rn-ai' : null,
      utm_medium: rnUtmAllowed && utmMap ? utmMedium : null,
      utm_campaign: rnUtmAllowed && utmMap ? safeCampaign(params?.get('utm_campaign')) : null,
    };
  };

  const cleanStoredSourceContext = (source) => {
    const value = source && typeof source === 'object' ? source : {};
    const host = normaliseToken(value.source_host);
    const medium = normaliseToken(value.utm_medium);
    return {
      source_family: ALLOWED_SOURCE_FAMILIES.has(normaliseToken(value.source_family))
        ? normaliseToken(value.source_family)
        : 'unknown',
      source_platform: ALLOWED_SOURCE_PLATFORMS.has(normaliseToken(value.source_platform))
        ? normaliseToken(value.source_platform)
        : 'unknown',
      source_surface: ALLOWED_SOURCE_SURFACES.has(normaliseToken(value.source_surface))
        ? normaliseToken(value.source_surface)
        : 'unknown',
      source_confidence: ALLOWED_SOURCE_CONFIDENCE.has(normaliseToken(value.source_confidence))
        ? normaliseToken(value.source_confidence)
        : 'observed_no_source',
      source_host: Object.prototype.hasOwnProperty.call(ALLOWED_SOURCE_HOSTS, host) ? host : null,
      utm_source: ['rn-ai', 'rn_ai'].includes(normaliseToken(value.utm_source)) ? 'rn-ai' : null,
      utm_medium: Object.prototype.hasOwnProperty.call(ALLOWED_UTM_MEDIUMS, medium) ? medium : null,
      utm_campaign: safeCampaign(value.utm_campaign),
    };
  };

  const cleanStoredTimestamp = (value) => {
    const timestamp = trim(value, 40);
    return /^\d{4}-\d{2}-\d{2}T[^\s]+Z$/.test(timestamp) && Number.isFinite(Date.parse(timestamp))
      ? timestamp
      : null;
  };

  const readStoredContext = () => {
    if (!publicStorefrontPath()) return null;
    const storage = sessionStorageIfAvailable();
    if (!storage) return null;
    try {
      const parsed = JSON.parse(storage.getItem(STORAGE_KEY) || 'null');
      if (!parsed || parsed.version !== CONTRACT_VERSION) return null;
      if (!/^rn1_[A-Za-z0-9_-]{32,60}$/.test(String(parsed.rn_visit_id || ''))) return null;
      if (parsed.consent_state !== 'accepted') return null;
      const cleanTouchpoints = Array.isArray(parsed.touchpoints)
        ? parsed.touchpoints.map((touchpoint) => {
          const path = safePath(touchpoint?.path || '/');
          const occurredAt = cleanStoredTimestamp(touchpoint?.occurred_at_utc);
          return occurredAt ? { path, occurred_at_utc: occurredAt } : { path };
        }).slice(-MAX_TOUCHPOINTS)
        : [];
      return {
        version: CONTRACT_VERSION,
        rn_visit_id: String(parsed.rn_visit_id),
        consent_state: 'accepted',
        first_landing_path: safePath(parsed.first_landing_path || '/'),
        last_landing_path: safePath(parsed.last_landing_path || '/'),
        world: Object.values(WORLD_VALUES).includes(normaliseToken(parsed.world))
          ? normaliseToken(parsed.world)
          : 'shared_or_unknown',
        last_product_handle: safeHandle(parsed.last_product_handle),
        source_context: cleanStoredSourceContext(parsed.source_context),
        selection_event_id: /^evt_[A-Za-z0-9_-]{1,96}$/.test(String(parsed.selection_event_id || ''))
          ? String(parsed.selection_event_id)
          : null,
        touchpoints: cleanTouchpoints,
      };
    } catch (_error) {
      return null;
    }
  };

  const writeContext = (context) => {
    if (!publicStorefrontPath()) return false;
    const storage = sessionStorageIfAvailable();
    if (!storage || !context) return false;
    try {
      storage.setItem(STORAGE_KEY, JSON.stringify(context));
      return true;
    } catch (_error) {
      return false;
    }
  };

  const makeContext = () => {
    const path = publicStorefrontPath();
    if (!path || consentState() !== 'accepted') return null;
    const visitId = randomVisitId();
    if (!visitId) return null;
    const context = {
      version: CONTRACT_VERSION,
      rn_visit_id: visitId,
      consent_state: 'accepted',
      first_landing_path: path,
      last_landing_path: path,
      world: currentWorld(),
      last_product_handle: handleFromPath(path),
      source_context: sourceContext(),
      selection_event_id: null,
      touchpoints: [{ path, occurred_at_utc: new Date().toISOString() }],
    };
    return writeContext(context) ? context : null;
  };

  const getContext = () => {
    const path = publicStorefrontPath();
    if (!path || consentState() !== 'accepted') return null;
    const context = readStoredContext() || makeContext();
    if (!context) return null;

    const source = sourceContext();
    context.consent_state = 'accepted';
    context.last_landing_path = path;
    context.world = currentWorld();
    context.last_product_handle = handleFromPath(path) || context.last_product_handle || null;
    if (source.source_confidence !== 'observed_no_source' || !context.source_context) {
      context.source_context = source;
    }
    if (!Array.isArray(context.touchpoints)) context.touchpoints = [];
    if (context.touchpoints[context.touchpoints.length - 1]?.path !== path) {
      context.touchpoints = [
        ...context.touchpoints,
        { path, occurred_at_utc: new Date().toISOString() },
      ].slice(-MAX_TOUCHPOINTS);
    }
    writeContext(context);
    return context;
  };

  const productContext = (element = null) => {
    const source = element?.closest?.('product-info, [data-rn-clarity-page]')
      || (!element && document.querySelector?.('product-info[data-product-id], [data-rn-clarity-page]'));
    const productId = productGid(source?.dataset?.productId);
    const variantInput = source?.querySelector?.('form [name="id"], [name="id"]')
      || document.querySelector?.('product-info form [name="id"], product-info [name="id"]');
    const variantId = variantGid(variantInput?.value);
    const path = publicStorefrontPath(source?.dataset?.url || window.location?.pathname || '/');
    if (!path) {
      return {
        route_id: null,
        route_path: null,
        product_id: null,
        variant_id: null,
        product_handle: null,
        family_id: null,
        cluster_id: null,
        world: null,
      };
    }
    const familyId = safeOwnerKey(source?.dataset?.rnFamilyId);
    const clusterId = safeClusterId(source?.dataset?.rnClusterId);
    return {
      route_id: routeId(path),
      route_path: path,
      product_id: productId,
      variant_id: variantId,
      product_handle: safeHandle(source?.dataset?.productHandle) || handleFromPath(path),
      family_id: familyId,
      cluster_id: clusterId,
      world: currentWorld(),
    };
  };

  const sanitiseValue = (key, value) => {
    if (key === 'form_id') return ['rn_personalisation', 'rn-lf-signup'].includes(value) || /^rn-list-signup-[A-Za-z0-9_-]{1,100}$/.test(value || '') ? value : null;
    if (key === 'signup_source') return ['footer', 'page', 'guide', 'presale', 'parcel'].includes(value) ? value : null;
    if (key === 'rn_photo_count') return Number.isInteger(value) && value >= 1 && value <= 5 ? value : null;
    if (key === 'page_path') return publicStorefrontPath(value);
    if (key === 'field_name') {
      return typeof value === 'string' && /^(photo_[1-5]|field_([1-9]|1[0-9]|20)|photos_later)$/.test(value) ? value : null;
    }
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'boolean') return value;
    if (typeof value !== 'string' && typeof value !== 'number') return null;
    const bounded = trim(value);
    if (!bounded) return null;
    if (key === 'rn_product_id' && !/^gid:\/\/shopify\/Product\/\d{1,30}$/.test(bounded)) return null;
    if (key === 'rn_variant_id' && !/^gid:\/\/shopify\/ProductVariant\/\d{1,30}$/.test(bounded)) return null;
    if (key === 'rn_family_id') return safeOwnerKey(bounded);
    if (key === 'rn_cluster_id') return safeClusterId(bounded);
    if (key === 'rn_visit_id' && !/^rn1_[A-Za-z0-9_-]{32,60}$/.test(bounded)) return null;
    if (key === 'rn_product_handle') {
      if (!/^[a-z0-9][a-z0-9-]{0,159}$/.test(normaliseToken(bounded))) return null;
      return normaliseToken(bounded);
    }
    if (key === 'rn_route_path' && safePath(bounded) !== bounded) return null;
    if (key === 'rn_route_id' && (!bounded.startsWith('route:/') || safePath(bounded.slice(6)) !== bounded.slice(6))) return null;
    if (key === 'rn_world') {
      if (!Object.values(WORLD_VALUES).includes(normaliseToken(bounded))) return null;
      return normaliseToken(bounded);
    }
    if (key === 'rn_source_family') {
      if (!ALLOWED_SOURCE_FAMILIES.has(normaliseToken(bounded))) return null;
      return normaliseToken(bounded);
    }
    if (key === 'rn_source_platform') {
      if (!ALLOWED_SOURCE_PLATFORMS.has(normaliseToken(bounded))) return null;
      return normaliseToken(bounded);
    }
    if (key === 'rn_source_surface') {
      if (!ALLOWED_SOURCE_SURFACES.has(normaliseToken(bounded))) return null;
      return normaliseToken(bounded);
    }
    if (key === 'rn_source_confidence') {
      if (!ALLOWED_SOURCE_CONFIDENCE.has(normaliseToken(bounded))) return null;
      return normaliseToken(bounded);
    }
    if (key === 'rn_source_host') {
      if (!Object.keys(ALLOWED_SOURCE_HOSTS).includes(normaliseToken(bounded))) return null;
      return normaliseToken(bounded);
    }
    if (key === 'rn_utm_source') {
      if (!['rn-ai', 'rn_ai'].includes(normaliseToken(bounded))) return null;
      return 'rn-ai';
    }
    if (key === 'rn_utm_medium') {
      if (!Object.prototype.hasOwnProperty.call(ALLOWED_UTM_MEDIUMS, normaliseToken(bounded))) return null;
      return normaliseToken(bounded);
    }
    if (key === 'rn_utm_campaign' && !safeCampaign(bounded)) return null;
    if (key === 'rn_consent_state') {
      if (!['accepted', 'rejected', 'unknown', 'internal'].includes(normaliseToken(bounded))) return null;
      return normaliseToken(bounded);
    }
    if (key === 'rn_surface' && bounded !== 'storefront') return null;
    if (key === 'rn_profile') {
      if (!ALLOWED_PROFILES.has(normaliseToken(bounded))) return null;
      return normaliseToken(bounded);
    }
    if (key === 'rn_input_type') {
      const values = bounded.split(',').map((token) => normaliseToken(token)).filter(Boolean);
      if (!values.length || values.some((token) => !ALLOWED_INPUT_TYPES.has(token))) return null;
      return Array.from(new Set(values)).sort().join(',');
    }
    if (key === 'rn_field_type') {
      if (!ALLOWED_INPUT_TYPES.has(normaliseToken(bounded))) return null;
      return normaliseToken(bounded);
    }
    if (key === 'module_id') return ['home_discovery', 'memorial_shelf', 'drop_a_hint', 'basket_hint'].includes(bounded) ? bounded : null;
    if (key === 'cta_label') {
      if (!ALLOWED_HOME_DISCOVERY_ACTIONS.has(normaliseToken(bounded)) && !ALLOWED_MEMORIAL_SHELF_ACTIONS.has(normaliseToken(bounded)) && !['open', 'share_sheet', 'whatsapp', 'email', 'copy'].includes(normaliseToken(bounded))) return null;
      return normaliseToken(bounded);
    }
    if (key === 'rn_selection_event_id' && !/^evt_[A-Za-z0-9_-]{1,96}$/.test(bounded)) return null;
    if (key === 'event_id' && !/^evt_[A-Za-z0-9_-]{1,96}$/.test(bounded)) return null;
    if (key === 'event_version' && bounded !== CONTRACT_VERSION) return null;
    if (key === 'occurred_at_utc' && (!/^\d{4}-\d{2}-\d{2}T[^\s]+Z$/.test(bounded) || !Number.isFinite(Date.parse(bounded)))) return null;
    return bounded;
  };

  const safeEventParameters = (name, parameters, context, id) => {
    if (NEWSLETTER_EVENTS.has(name)) {
      // Newsletter counts carry only fixed form/source values and the public
      // pathname. Never forward form values, query strings or product context.
      return {
        send_to: GA4_MEASUREMENT_ID,
        form_id: sanitiseValue('form_id', parameters.form_id),
        signup_source: sanitiseValue('signup_source', parameters.signup_source),
        page_path: publicStorefrontPath(),
      };
    }
    const product = productContext();
    const source = context.source_context || {};
    const base = {
      send_to: GA4_MEASUREMENT_ID,
      rn_surface: 'storefront',
      rn_route_id: product.route_id,
      rn_route_path: product.route_path,
      rn_product_id: product.product_id,
      rn_variant_id: product.variant_id,
      rn_product_handle: product.product_handle,
      rn_family_id: product.family_id,
      rn_cluster_id: product.cluster_id,
      rn_world: product.world,
      rn_source_family: source.source_family,
      rn_source_platform: source.source_platform,
      rn_source_surface: source.source_surface,
      rn_source_confidence: source.source_confidence,
      rn_source_host: source.source_host,
      rn_utm_source: source.utm_source,
      rn_utm_medium: source.utm_medium,
      rn_utm_campaign: source.utm_campaign,
    };
    if (name === 'rn_guide_product_click') {
      // A guide click names its target explicitly. Do not carry a variant or
      // functional owner from a stale PDP DOM/context into that diagnostic.
      GUIDE_PRODUCT_CONTEXT_KEYS.forEach((key) => { base[key] = null; });
    }
    const candidate = {
      ...base,
      ...parameters,
      // These fields are owned by this module and cannot be overridden by a
      // feature-specific parameter object.
      send_to: GA4_MEASUREMENT_ID,
      event_id: id,
      event_version: CONTRACT_VERSION,
      occurred_at_utc: new Date().toISOString(),
      rn_visit_id: context.rn_visit_id,
      rn_consent_state: context.consent_state,
    };
    const result = { send_to: GA4_MEASUREMENT_ID };
    Object.keys(candidate).forEach((key) => {
      if (key === 'send_to' || !SAFE_PARAMETERS.has(key)) return;
      const value = sanitiseValue(key, candidate[key]);
      if (value !== null) {
        result[key] = value;
        return;
      }
      // An invalid feature parameter must not erase a valid route/product
      // context owned by this adapter. This also keeps required context
      // available when a caller accidentally includes a query string.
      const fallback = sanitiseValue(key, base[key]);
      if (fallback !== null) result[key] = fallback;
    });
    return result;
  };

  // Route (11 Oct 2026, job j1011-040657-c186): the page gtag first.
  // The Google & YouTube app defines window.gtag on the page (consent mode on),
  // and that gtag delivers to our GA4 stream. The "RN events to GA4" custom pixel
  // runs in a sandbox iframe without cookies; its gtag loads but never sends, so
  // every rn_ event was lost from 7 Oct. The bus stays only as a last resort.
  // One route per event, so nothing is counted twice.
  const shopifyBus = () => {
    const a = window.Shopify && window.Shopify.analytics;
    return a && typeof a.publish === 'function' ? a : null;
  };
  const pageGtag = () => (typeof window.gtag === 'function' ? window.gtag : null);
  const canSend = () => !!shopifyBus() || !!pageGtag();
  const publishToBus = (name, params) => {
    const bus = shopifyBus();
    if (!bus) return;
    const data = {};
    Object.keys(params).forEach((key) => { if (key !== 'send_to') data[key] = params[key]; });
    bus.publish(name.indexOf('rn_') === 0 || name.indexOf('newsletter_') === 0 ? name : `rn_${name}`, data);
  };
  // The app's gtag can appear after our first events (it starts once consent is
  // read). Hold events briefly and flush them in order when it appears.
  const pendingSends = [];
  let pendingTimer = null;
  let pendingSince = 0;
  const flushPending = () => {
    const gtag = pageGtag();
    if (!gtag && Date.now() - pendingSince < 15000) return;
    window.clearInterval(pendingTimer);
    pendingTimer = null;
    pendingSends.splice(0).forEach(([name, params]) => {
      try {
        if (gtag) gtag('event', name, params);
        else publishToBus(name, params);
      } catch (_error) { /* one failed event must not block the rest */ }
    });
  };
  const send = (name, params) => {
    const gtag = pageGtag();
    if (gtag && !pendingSends.length) {
      gtag('event', name, params);
      return;
    }
    pendingSends.push([name, params]);
    if (!pendingTimer) {
      pendingSince = Date.now();
      pendingTimer = window.setInterval(flushPending, 250);
    }
  };

  const emit = (name, parameters = {}, explicitEventId = '') => {
    if (!DIAGNOSTIC_EVENTS.has(name) || !publicStorefrontPath() || consentState() !== 'accepted') return null;
    if (NEWSLETTER_EVENTS.has(name) && !(
      (parameters.form_id === 'rn-lf-signup' && parameters.signup_source === 'footer')
      || (name === 'newsletter_signup' && /^rn-list-signup-[A-Za-z0-9_-]{1,100}$/.test(parameters.form_id || '') && ['page', 'guide', 'presale', 'parcel'].includes(parameters.signup_source))
    )) return null;
    const context = getContext();
    if (!context || (!PERSONALISATION_FORM_EVENTS.has(name) && !canSend())) return null;
    const id = /^evt_[A-Za-z0-9_-]{1,96}$/.test(explicitEventId)
      ? explicitEventId
      : eventId('evt');
    const dedupeKey = `${name}:${id}`;
    if (emittedKeys.has(dedupeKey)) return id;
    const payload = safeEventParameters(name, parameters, context, id);
    try {
      emittedKeys.add(dedupeKey);
      if (PERSONALISATION_FORM_EVENTS.has(name)) {
        // Keep form diagnostics separate from automatic/standard commerce
        // tracking. Only fixed field keys and public product context leave.
        const formPayload = {};
        ['send_to', 'event_id', 'form_id', 'field_name', 'rn_product_handle', 'rn_profile'].forEach((key) => {
          if (payload[key] !== undefined) formPayload[key] = payload[key];
        });
        if (canSend()) send(name, formPayload);
        else (function(){(window.dataLayer=window.dataLayer||[]).push(arguments);})('event', name, formPayload);
      } else {
        send(name, payload);
      }
      // A later Clarity failure must not replay an already-dispatched signup.
      if (NEWSLETTER_EVENTS.has(name)) return id;
      if (typeof window.clarity === 'function') window.clarity('event', name);
      return id;
    } catch (_error) {
      emittedKeys.delete(dedupeKey);
      return null;
    }
  };

  const recordGuideProductClick = (parameters = {}) => {
    const context = getContext();
    if (!context) return null;
    // Two scripts can record the same click on a guide hub (rn-hyper-conversion.js
    // and the guide section). Count one click per product per second.
    const clickKey = `${window.location.pathname}|${parameters.rn_product_handle || ''}`;
    if (state.lastGuideClick && state.lastGuideClick.key === clickKey && Date.now() - state.lastGuideClick.at < 1000) {
      return state.lastGuideClick.id;
    }
    const id = eventId('evt');
    const emittedId = emit('rn_guide_product_click', {
      ...parameters,
      rn_selection_event_id: id,
    }, id);
    if (!emittedId) return null;
    state.lastGuideClick = { key: clickKey, at: Date.now(), id: emittedId };
    context.selection_event_id = emittedId;
    writeContext(context);
    return emittedId;
  };

  const selectionContext = () => {
    const context = getContext();
    return context ? { selection_event_id: context.selection_event_id || null } : null;
  };

  const cartPayload = (context) => {
    const attributes = {
      rn_visit_id: context.rn_visit_id,
      attribution_version: CONTRACT_VERSION,
      // Send an explicit null when there was no observed guide selection so a
      // stale selection from an earlier session cannot be joined to this one.
      selection_event_id: context.selection_event_id || null,
    };
    return attributes;
  };

  const cartSignature = (attributes) => JSON.stringify(attributes);

  const persistCartAttribution = () => {
    const context = getContext();
    if (!context || typeof window.fetch !== 'function') return Promise.resolve(false);
    const attributes = cartPayload(context);
    const signature = cartSignature(attributes);
    if (state.persistedSignature === signature) return Promise.resolve(true);
    if (state.persistPromise) return state.persistPromise;

    state.persistPromise = new Promise((resolve) => {
      let settled = false;
      let timer = null;
      let controller = null;
      const finish = (value) => {
        if (settled) return;
        settled = true;
        if (timer) window.clearTimeout(timer);
        if (value) state.persistedSignature = signature;
        resolve(Boolean(value));
      };

      if (typeof window.AbortController === 'function') controller = new window.AbortController();
      timer = window.setTimeout(() => {
        try { controller?.abort(); } catch (_error) {}
        finish(false);
      }, CART_TIMEOUT_MS);

      try {
        window.fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          keepalive: true,
          headers: { 'content-type': 'application/json', accept: 'application/json' },
          body: JSON.stringify({ attributes }),
          ...(controller ? { signal: controller.signal } : {}),
        })
          .then((response) => {
            if (!response?.ok) throw new Error('Cart attribution update failed');
            return response.json();
          })
          .then((cart) => {
            const returned = cart?.attributes || {};
            const returnedSelection = returned.selection_event_id || null;
            finish(
              returned.rn_visit_id === context.rn_visit_id
                && returned.attribution_version === CONTRACT_VERSION
                && returnedSelection === (context.selection_event_id || null),
            );
          })
          .catch(() => finish(false));
      } catch (_error) {
        finish(false);
      }
    }).finally(() => {
      state.persistPromise = null;
    });
    return state.persistPromise;
  };

  const clearWorldEntryRetry = () => {
    if (state.worldEntryRetryTimer !== null) {
      try { window.clearTimeout(state.worldEntryRetryTimer); } catch (_error) {}
      state.worldEntryRetryTimer = null;
    }
    state.worldEntryRetryIndex = 0;
  };

  const scheduleWorldEntryRetry = () => {
    if (state.worldEntryRetryTimer !== null) return;
    if (!publicStorefrontPath() || consentState() !== 'accepted') return;
    const delay = WORLD_ENTRY_RETRY_DELAYS_MS[state.worldEntryRetryIndex];
    if (delay === undefined) return;
    state.worldEntryRetryIndex += 1;
    try {
      state.worldEntryRetryTimer = window.setTimeout(() => {
        state.worldEntryRetryTimer = null;
        // Re-check the public/consent boundary on every retry. A private or
        // rejected navigation must never inherit this pending observation.
        if (!publicStorefrontPath() || consentState() !== 'accepted') {
          clearWorldEntryRetry();
          return;
        }
        const context = getContext();
        if (!context || !canSend()) {
          scheduleWorldEntryRetry();
          return;
        }
        recordWorldEntry();
      }, delay);
    } catch (_error) {
      state.worldEntryRetryTimer = null;
    }
  };

  const recordWorldEntry = () => {
    if (!publicStorefrontPath() || consentState() !== 'accepted') {
      clearWorldEntryRetry();
      return null;
    }
    const context = getContext();
    if (!context || !canSend()) {
      scheduleWorldEntryRetry();
      return null;
    }
    clearWorldEntryRetry();
    const world = context.world || 'shared_or_unknown';
    if (state.worldEntryRecorded.has(world)) return null;

    const storage = sessionStorageIfAvailable();
    let storedEventId = null;
    try {
      const candidate = storage?.getItem(`${WORLD_ENTRY_STORAGE_PREFIX}${world}`) || '';
      if (/^evt_[A-Za-z0-9_-]{1,96}$/.test(candidate)) storedEventId = candidate;
    } catch (_error) {}
    if (storedEventId) {
      state.worldEntryEventIds.set(world, storedEventId);
      state.worldEntryRecorded.add(world);
      return null;
    }

    const id = state.worldEntryEventIds.get(world) || eventId('evt');
    state.worldEntryEventIds.set(world, id);
    const recorded = emit('rn_world_entry', {}, id);
    if (recorded) {
      state.worldEntryRecorded.add(world);
      try { storage?.setItem(`${WORLD_ENTRY_STORAGE_PREFIX}${world}`, recorded); } catch (_error) {}
    }
    return recorded;
  };

  // Area 8: native help/share interactions use the same consent and payload
  // guard as other diagnostics. No link query, form value or customer text
  // is collected. These events describe actions, never referrals or sales.
  document.addEventListener('toggle', (event) => {
    const details = event.target;
    if (details?.id?.startsWith('Details-customer_help-') && details.open) {
      emit('rn_customer_help_open');
    }
  }, true);
  document.addEventListener('click', (event) => {
    const target = event.target?.closest?.('button, a');
    if (!target) return;
    if (target.matches('button.btn--plain[aria-controls^="PopupModal-Sharing-"]')) {
      emit('rn_product_share_open');
      return;
    }
    if (target.matches('product-share .btn-copy')) {
      emit('rn_product_link_copy_attempt');
      return;
    }
    if (target.tagName !== 'A') return;
    // Memorial page shelves: which shelf was clicked, and which piece. Only
    // the fixed shelf name and the public product handle leave the page.
    const memorialShelf = target.closest('[data-rn-memorial-shelf]');
    if (memorialShelf) {
      const shelf = memorialShelf.dataset.rnMemorialShelf;
      const seeAll = Boolean(target.closest('.rn-memorial-shelf__all'));
      const handle = /^\/products\/([a-z0-9-]+)$/.exec(safePath(target.getAttribute('href')));
      emit('rn_memorial_shelf_click', {
        module_id: 'memorial_shelf',
        cta_label: seeAll ? `see_all_${shelf}` : shelf,
        ...(handle ? { rn_product_handle: handle[1] } : {}),
      });
      return;
    }
    if (/^https:\/\/orders\.ripleysnest\.co\.uk\/gift-ideas\/start(?:[?#]|$)/.test(target.href || '')) {
      const source = target.href.includes('source=header') ? 'header' : target.href.includes('source=footer') ? 'footer' : 'homepage';
      emit('rn_imagination_factory_click', { module_id: 'imagination_factory', cta_label: source });
      return;
    }
    const helpScope = target.closest('details[id^="Details-customer_help-"]')
      || (window.location.pathname === '/pages/help-support' && target.closest('main'));
    if (!helpScope) return;
    const path = safePath(target.getAttribute('href'));
    if (['/pages/cast-stone-care', '/pages/rat-cage-cleaning-guide'].includes(path)) {
      emit('rn_care_guide_click');
    } else if (['/pages/help-support', '/pages/contact', '/pages/order-tracking', '/pages/shipping', '/pages/returns', '/account'].includes(path)) {
      emit('rn_customer_help_click');
    }
  }, true);

  // First Area 8 outcome pilot: the existing native Home discovery block
  // on the Disco collection. The source route remains adapter-owned; only
  // five existing destinations can enter rn_action. Nothing is queued before
  // consent and every attempt passes through emit's current-consent guard.
  const initHomeDiscoveryPilot = () => {
    if (window.location.pathname !== '/collections/disco') return;
    const root = document.querySelector('[id^="shopify-section-"][id$="__home_discovery"]');
    if (!root || root.dataset.rnHomePilotBound === 'true') return;
    root.dataset.rnHomePilotBound = 'true';
    const destinations = Object.freeze({
      '/collections/disco': 'collection_disco',
      '/collections/sculpted-planters': 'collection_sculpted_planters',
      '/collections/planters-home-decor': 'collection_planters_home_decor',
      '/products/the-disco-era-mirror-ball-bust-wall-art': 'product_disco_era_bust',
      '/products/golden-hour-cast-stone-head-planter': 'product_golden_hour_planter',
    });
    let visible = false;
    let impressionRecorded = false;
    const recordVisibleImpression = () => {
      if (!visible || impressionRecorded) return;
      impressionRecorded = Boolean(emit('rn_home_discovery_impression', { module_id: 'home_discovery' }));
    };
    if (typeof window.IntersectionObserver === 'function') {
      const observer = new window.IntersectionObserver((entries) => {
        visible = entries.some((entry) => entry.target === root && entry.isIntersecting && entry.intersectionRatio >= 0.5);
        recordVisibleImpression();
        if (impressionRecorded) observer.disconnect();
      }, { threshold: [0.5] });
      observer.observe(root);
    }
    root.addEventListener('click', (event) => {
      const anchor = event.target?.closest?.('a[href]');
      if (!anchor || !root.contains(anchor)) return;
      const action = destinations[safePath(anchor.getAttribute('href'))];
      if (action) emit('rn_home_discovery_destination_click', { module_id: 'home_discovery', cta_label: action });
    });
    document.addEventListener('visitorConsentCollected', recordVisibleImpression);
    document.addEventListener('trackingConsentAccepted', recordVisibleImpression);
  };

  window.rnMeasure = (name, parameters = {}) => emit(name, parameters);
  window.rnRecordGuideProductClick = recordGuideProductClick;
  window.rnGetAttributionContext = () => {
    const context = getContext();
    return context ? JSON.parse(JSON.stringify(context)) : null;
  };
  window.rnGetProductContext = productContext;
  window.rnGetSelectionContext = selectionContext;
  window.rnPersistCartAttribution = persistCartAttribution;
  window.rnRecordWorldEntry = recordWorldEntry;

  document.addEventListener('visitorConsentCollected', recordWorldEntry);
  document.addEventListener('trackingConsentAccepted', recordWorldEntry);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', recordWorldEntry, { once: true });
    document.addEventListener('DOMContentLoaded', initHomeDiscoveryPilot, { once: true });
  } else {
    recordWorldEntry();
    initHomeDiscoveryPilot();
  }
})();
